import type { Lead } from '@up2stay/up2promo-api-client';
import { Checkbox, cn } from '@up2stay/up2promo-ui';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';
import {
  ArrowLeftRight,
  Calendar,
  FileText,
  Lock,
  Mail,
  Pencil,
  Phone,
  ScanLine,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';

import type { LeadCardMetaLines } from '@/core/components/leads/lead-card-content';
import { getLeadCardLabelChips } from '@/core/components/leads/lead-card-label-chips';
import type { LeadCardLabelMaps } from '@/core/components/leads/lead-card-labels';
import type { LeadAssigneePickerLabels } from '@/core/components/leads/lead-assignee-picker';
import { LeadStatusBadge } from '@/core/components/leads/lead-detail/lead-status-badge';
import {
  LeadsListRowContextMenu,
  type LeadsListContextMenuLabels,
} from '@/core/components/leads/leads-list-context-menu';
import {
  formatLeadCapturedAt,
  leadCompanyLine,
  leadInitials,
  leadSourceVisual,
  personInitials,
} from '@/core/components/leads/leads-list-display';
import {
  resourceJustCreatedCardClass,
  resourceQuickMenuActiveCardClass,
} from '@/core/components/layout/list-toolbar-styles';
import { HighlightMatchText } from '@/core/components/ui/highlight-match-text';

/**
 * Columns follow the table's own width (container `leads`), not the viewport:
 * narrow tables (tablet, open sidebar) drop the low-value columns first so the
 * contact name stays readable. Full set from 880px, origin + captured from 680px.
 */
const DESKTOP_GRID =
  'grid grid-cols-[28px_minmax(0,2fr)_118px_96px] @min-[680px]/leads:grid-cols-[28px_minmax(0,2.1fr)_118px_minmax(0,1.5fr)_96px_96px] @min-[880px]/leads:grid-cols-[28px_minmax(0,2.1fr)_76px_118px_minmax(0,1.5fr)_96px_82px_96px] gap-3 items-center';
const FROM_MID = 'hidden @min-[680px]/leads:block';
const FROM_WIDE = 'hidden @min-[880px]/leads:block';

export type LeadsListTableContextMenuProps = {
  workspaceId: string;
  locale: string;
  selectedLeadIds: ReadonlySet<string>;
  selectedLeads: Lead[];
  assigneePickerLabels: LeadAssigneePickerLabels;
  existingTags: string[];
  labels: LeadsListContextMenuLabels;
  canExport: boolean;
  workspaceLeadsAlwaysShared?: boolean;
  onExportLeads: (leads: Lead[]) => void;
  onRefresh: () => void;
};

type LeadsListTableProps = {
  leads: Lead[];
  locale: string;
  searchQuery?: string;
  highlightedLeadId?: string | null;
  selectionEnabled?: boolean;
  selectedLeadIds?: ReadonlySet<string>;
  onToggleLeadSelect?: (leadId: string) => void;
  selectAriaLabel?: string;
  onLeadClick: (lead: Lead) => void;
  leadCardMeta?: (lead: Lead) => LeadCardMetaLines | null | undefined;
  labelMaps?: LeadCardLabelMaps;
  statusLabels: Record<string, string>;
  sourceLabels: Record<string, string>;
  columnLabels: {
    contact: string;
    status: string;
    origin: string;
    assignee: string;
    visibility: string;
    captured: string;
  };
  variant: 'desktop' | 'mobile';
  footer?: ReactNode;
  headerCheckbox?: ReactNode;
  contextMenu?: LeadsListTableContextMenuProps;
};

export function LeadsListTable({
  leads,
  locale,
  searchQuery = '',
  highlightedLeadId = null,
  selectionEnabled = false,
  selectedLeadIds,
  onToggleLeadSelect,
  selectAriaLabel = 'Select {name}',
  onLeadClick,
  leadCardMeta,
  labelMaps,
  statusLabels,
  sourceLabels,
  columnLabels,
  variant,
  footer,
  headerCheckbox,
  contextMenu,
}: LeadsListTableProps) {
  if (variant === 'mobile') {
    return (
      <div className="overflow-hidden rounded-[var(--radius-card-mobile)] border border-[color:var(--surface-border)] bg-card shadow-[var(--shadow-card)]">
        {leads.map((lead, index) => {
          const row = (highlighted: boolean) => (
            <MobileLeadRow
              lead={lead}
              locale={locale}
              searchQuery={searchQuery}
              highlighted={highlightedLeadId === lead.id || highlighted}
              bordered={index > 0}
              onLeadClick={contextMenu ? undefined : onLeadClick}
              labelMaps={labelMaps}
              statusLabels={statusLabels}
            />
          );

          if (!contextMenu) {
            return <div key={lead.id}>{row(false)}</div>;
          }

          return (
            <LeadsListRowContextMenu
              key={lead.id}
              lead={lead}
              variant="mobile"
              workspaceId={contextMenu.workspaceId}
              locale={contextMenu.locale}
              selectedLeadIds={contextMenu.selectedLeadIds}
              selectedLeads={contextMenu.selectedLeads}
              statusLabels={statusLabels}
              assigneeLabel={leadCardMeta?.(lead)?.assigneeValue}
              assigneePickerLabels={contextMenu.assigneePickerLabels}
              existingTags={contextMenu.existingTags}
              labels={contextMenu.labels}
              canExport={contextMenu.canExport}
              workspaceLeadsAlwaysShared={
                contextMenu.workspaceLeadsAlwaysShared
              }
              onOpenLead={onLeadClick}
              onExportLeads={contextMenu.onExportLeads}
              onRefresh={contextMenu.onRefresh}
            >
              {({ highlighted }) => row(highlighted)}
            </LeadsListRowContextMenu>
          );
        })}
        {footer}
      </div>
    );
  }

  return (
    <div className="@container/leads min-w-0">
      <div
        className={cn(
          DESKTOP_GRID,
          'border-b border-[color:var(--surface-border)] px-[18px] py-2.5 text-[11.5px] text-muted-foreground',
        )}
      >
        <div className="flex items-center justify-center">{headerCheckbox}</div>
        <div className="truncate">{columnLabels.contact}</div>
        <div className={FROM_WIDE} />
        <div className="truncate">{columnLabels.status}</div>
        <div className={cn(FROM_MID, 'truncate')}>{columnLabels.origin}</div>
        <div className="truncate">{columnLabels.assignee}</div>
        <div className={cn(FROM_WIDE, 'truncate')}>
          {columnLabels.visibility}
        </div>
        <div className={cn(FROM_MID, 'truncate text-right')}>
          {columnLabels.captured}
        </div>
      </div>
      {leads.map((lead, index) => {
        const row = (
          <DesktopLeadRow
            lead={lead}
            locale={locale}
            searchQuery={searchQuery}
            highlighted={highlightedLeadId === lead.id}
            bordered={index > 0}
            selectionEnabled={selectionEnabled}
            selected={selectedLeadIds?.has(lead.id) ?? false}
            onToggleSelect={onToggleLeadSelect}
            selectAriaLabel={selectAriaLabel}
            onLeadClick={onLeadClick}
            meta={leadCardMeta?.(lead)}
            labelMaps={labelMaps}
            statusLabels={statusLabels}
            sourceLabels={sourceLabels}
          />
        );

        if (!contextMenu) {
          return <div key={lead.id}>{row}</div>;
        }

        return (
          <LeadsListRowContextMenu
            key={lead.id}
            lead={lead}
            variant="desktop"
            workspaceId={contextMenu.workspaceId}
            locale={contextMenu.locale}
            selectedLeadIds={contextMenu.selectedLeadIds}
            selectedLeads={contextMenu.selectedLeads}
            statusLabels={statusLabels}
            assigneeLabel={leadCardMeta?.(lead)?.assigneeValue}
            assigneePickerLabels={contextMenu.assigneePickerLabels}
            existingTags={contextMenu.existingTags}
            labels={contextMenu.labels}
            canExport={contextMenu.canExport}
            workspaceLeadsAlwaysShared={contextMenu.workspaceLeadsAlwaysShared}
            onOpenLead={onLeadClick}
            onExportLeads={contextMenu.onExportLeads}
            onRefresh={contextMenu.onRefresh}
          >
            {() => row}
          </LeadsListRowContextMenu>
        );
      })}
      {footer}
    </div>
  );
}

function DesktopLeadRow({
  lead,
  locale,
  searchQuery,
  highlighted,
  bordered,
  selectionEnabled,
  selected,
  onToggleSelect,
  selectAriaLabel,
  onLeadClick,
  meta,
  labelMaps,
  statusLabels,
  sourceLabels,
}: {
  lead: Lead;
  locale: string;
  searchQuery: string;
  highlighted: boolean;
  bordered: boolean;
  selectionEnabled: boolean;
  selected: boolean;
  onToggleSelect?: (leadId: string) => void;
  selectAriaLabel: string;
  onLeadClick: (lead: Lead) => void;
  meta?: LeadCardMetaLines | null;
  labelMaps?: LeadCardLabelMaps;
  statusLabels: Record<string, string>;
  sourceLabels: Record<string, string>;
}) {
  const name =
    formatLeadDisplayName(lead.firstName, lead.lastName) || lead.email || '—';
  const company = leadCompanyLine(lead, true);
  const sourceLabel = sourceLabels[lead.source] ?? lead.source;
  const sourceVisual = leadSourceVisual(lead.source);
  const assigneeName = meta?.assigneeValue?.trim() || '—';
  const visibility = meta?.visibility;
  const captured = formatLeadCapturedAt(lead.createdAt, locale);

  return (
    <div
      data-lead-id={lead.id}
      data-just-created={highlighted ? 'true' : undefined}
      role="button"
      tabIndex={0}
      className={cn(
        DESKTOP_GRID,
        'cursor-pointer px-[18px] py-[11px] text-left transition-colors hover:bg-[color:var(--surface-2)]',
        bordered && 'border-t border-[color:var(--surface-border)]',
        highlighted && resourceJustCreatedCardClass,
        selected && 'bg-[color:var(--ghost-accent-bg)]/35',
      )}
      onClick={() => onLeadClick(lead)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onLeadClick(lead);
        }
      }}
    >
      <div
        className="flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {selectionEnabled ? (
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelect?.(lead.id)}
            aria-label={selectAriaLabel.replace('{name}', name)}
            className="size-4 rounded-[5px]"
          />
        ) : (
          <span
            className="size-4 rounded-[5px] border-[1.5px] border-[color:var(--surface-border)]"
            aria-hidden
          />
        )}
      </div>

      <div className="flex min-w-0 items-center gap-[11px]">
        <span className="inline-flex size-[34px] shrink-0 items-center justify-center rounded-[10px] bg-muted text-[12px] font-bold text-foreground">
          {leadInitials(lead)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-[13.5px] font-semibold text-foreground">
            <HighlightMatchText text={name} query={searchQuery} />
          </div>
          {company ? (
            <div className="truncate text-xs text-muted-foreground">
              <HighlightMatchText text={company} query={searchQuery} />
            </div>
          ) : null}
        </div>
      </div>

      <div className="hidden gap-1.5 text-muted-foreground @min-[880px]/leads:flex">
        {lead.email ? <Mail className="size-[15px]" aria-hidden /> : null}
        {lead.phone ? <Phone className="size-[15px]" aria-hidden /> : null}
      </div>

      <div>
        <LeadStatusBadge
          status={lead.status}
          label={statusLabels[lead.status] ?? lead.status}
          className="h-[22px] rounded-full px-2 text-[11.5px] font-semibold"
        />
      </div>

      <div className="hidden min-w-0 flex-wrap items-center gap-2 overflow-hidden @min-[680px]/leads:flex">
        <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-[12.5px]">
          <SourceIcon kind={sourceVisual.icon} />
          <span className="truncate">{sourceLabel}</span>
        </span>
        <LeadEventChips lead={lead} maps={labelMaps} />
      </div>

      <div className="flex min-w-0 items-center gap-1.5 text-[12.5px]">
        <span className="inline-flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[color:var(--ghost-accent-bg)] text-[9px] font-bold text-[color:var(--ghost-accent-fg)]">
          {personInitials(assigneeName)}
        </span>
        <span className="truncate">
          {assigneeName.split(/\s+/)[0] ?? assigneeName}
        </span>
      </div>

      <div className={FROM_WIDE}>
        {visibility === 'private' ? (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
            <Lock className="size-[13px]" aria-hidden />
            {meta?.visibilityPrivateLabel ?? 'Privat'}
          </span>
        ) : visibility === 'shared' ? (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
            <Users className="size-[13px]" aria-hidden />
            {meta?.visibilitySharedLabel ?? 'Workspace'}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </div>

      <div
        className={cn(
          FROM_MID,
          'text-right text-xs whitespace-nowrap text-muted-foreground',
        )}
      >
        {captured}
      </div>
    </div>
  );
}

function MobileLeadRow({
  lead,
  locale,
  searchQuery,
  highlighted,
  bordered,
  onLeadClick,
  labelMaps,
  statusLabels,
}: {
  lead: Lead;
  locale: string;
  searchQuery: string;
  highlighted: boolean;
  bordered: boolean;
  onLeadClick?: (lead: Lead) => void;
  labelMaps?: LeadCardLabelMaps;
  statusLabels: Record<string, string>;
}) {
  const name =
    formatLeadDisplayName(lead.firstName, lead.lastName) || lead.email || '—';
  const company = leadCompanyLine(lead, false);
  const captured = formatLeadCapturedAt(lead.createdAt, locale);
  const isPrivate = lead.visibility === 'private';

  const className = cn(
    'flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors',
    bordered && 'border-t border-[color:var(--surface-border)]',
    highlighted && resourceJustCreatedCardClass,
    highlighted && onLeadClick == null && resourceQuickMenuActiveCardClass,
  );

  const body = (
    <>
      <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-[13px] font-bold text-foreground">
        {leadInitials(lead)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5 text-[14px] font-semibold leading-tight">
            {lead.status === 'new' ? (
              <span
                className="size-[7px] shrink-0 rounded-full bg-[color:var(--primary)]"
                aria-hidden
              />
            ) : null}
            <span className="truncate">
              <HighlightMatchText text={name} query={searchQuery} />
            </span>
          </span>
          <span className="shrink-0 pt-0.5 text-[11.5px] text-muted-foreground">
            {captured}
          </span>
        </div>
        {company ? (
          <div className="mt-0.5 truncate text-[12.5px] text-muted-foreground">
            <HighlightMatchText text={company} query={searchQuery} />
          </div>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <LeadStatusBadge
            status={lead.status}
            label={statusLabels[lead.status] ?? lead.status}
            className="h-[22px] rounded-full px-2 text-[11.5px] font-semibold"
          />
          <LeadEventChips lead={lead} maps={labelMaps} />
          {isPrivate ? (
            <Lock className="size-[13px] text-muted-foreground" aria-hidden />
          ) : null}
        </div>
      </div>
    </>
  );

  if (!onLeadClick) {
    return (
      <div data-lead-id={lead.id} className={className}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-lead-id={lead.id}
      className={className}
      onClick={() => onLeadClick(lead)}
    >
      {body}
    </button>
  );
}

function LeadEventChips({
  lead,
  maps,
}: {
  lead: Lead;
  maps?: LeadCardLabelMaps;
}) {
  const { events } = getLeadCardLabelChips(lead, maps);
  if (events.length === 0) return null;
  return (
    <>
      {events.slice(0, 1).map((name) => (
        <span
          key={name}
          className="inline-flex h-5 max-w-full min-w-0 items-center gap-1 rounded-md bg-muted px-1.5 text-[11px] whitespace-nowrap"
        >
          <Calendar className="size-[11px] shrink-0" aria-hidden />
          <span className="truncate">{name}</span>
        </span>
      ))}
    </>
  );
}

function SourceIcon({
  kind,
}: {
  kind: 'form' | 'scan' | 'exchange' | 'manual';
}) {
  const className = 'size-[13px] shrink-0 text-muted-foreground';
  switch (kind) {
    case 'form':
      return <FileText className={className} aria-hidden />;
    case 'scan':
      return <ScanLine className={className} aria-hidden />;
    case 'exchange':
      return <ArrowLeftRight className={className} aria-hidden />;
    default:
      return <Pencil className={className} aria-hidden />;
  }
}
